import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Gift, Dumbbell, Bell, MessageSquare, Copy, Check, UserPlus, RefreshCw, Edit3, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { daysUntilExpiry, isBirthday, formatDate, formatPhone } from '../utils/helpers';
import { format, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';

const SmsButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      title="문자 템플릿 복사"
      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-colors ${
        copied
          ? 'bg-green-50 text-green-600 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
          : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
      }`}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? '복사됨' : '문자'}
    </button>
  );
};

const Section = ({ icon: Icon, title, color, count, children }) => (
  <div className="card overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h2 className="font-semibold text-gray-900 dark:text-white">{title}</h2>
      </div>
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{count}건</span>
    </div>
    <div className="p-2">
      {children}
    </div>
  </div>
);

const Notifications = () => {
  const navigate = useNavigate();
  const {
    members, memberships, ptAssignments, gymSettings,
    renewalRequests, profileEditRequests,
    approveMember, rejectMember,
    approveRenewal, rejectRenewal,
    approveProfileEdit, rejectProfileEdit,
  } = useApp();
  const gymName = gymSettings?.name || '헬스장';

  // 가입 신청 대기 (pending members)
  const pendingMembers = members.filter(m => m.status === 'pending');

  // 연장 신청 대기
  const pendingRenewals = (renewalRequests || []).filter(r => r.status === 'pending');

  // 정보 수정 신청 대기
  const pendingProfileEdits = (profileEditRequests || []).filter(r => r.status === 'pending');

  // 만료 임박 (7일 이내)
  const expiringMemberships = memberships
    .map(ms => {
      const days = daysUntilExpiry(ms.endDate);
      const member = members.find(m => m.id === ms.memberId);
      return { ...ms, days, member };
    })
    .filter(ms => ms.days !== null && ms.days >= 0 && ms.days <= 7 && ms.status !== 'expired' && ms.member)
    .sort((a, b) => a.days - b.days);

  // 오늘 생일
  const birthdayMembers = members.filter(m => m.status !== 'pending' && isBirthday(m.birthDate));

  // PT 횟수 부족 (5회 이하)
  const lowPtMembers = ptAssignments
    .filter(p => p.status === 'active' && (p.totalSessions - p.usedSessions) <= 5)
    .map(p => {
      const member = members.find(m => m.id === p.memberId);
      return { ...p, member, remaining: p.totalSessions - p.usedSessions };
    })
    .filter(p => p.member)
    .sort((a, b) => a.remaining - b.remaining);

  const totalCount = pendingMembers.length + pendingRenewals.length + pendingProfileEdits.length +
    expiringMemberships.length + birthdayMembers.length + lowPtMembers.length;

  return (
    <div className="space-y-6 max-w-3xl">
      {totalCount === 0 && (
        <div className="card p-12 text-center">
          <Bell className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">현재 알림이 없습니다.</p>
        </div>
      )}

      {/* 가입 신청 대기 */}
      {pendingMembers.length > 0 && (
        <Section icon={UserPlus} title="가입 신청 대기" color="bg-indigo-500" count={pendingMembers.length}>
          {pendingMembers.map(member => (
            <div key={member.id} className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">{member.name[0]}</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{member.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatPhone(member.phone)}
                    {member.purpose && ` · ${member.purpose}`}
                  </p>
                  {member.createdAt && (
                    <p className="text-xs text-gray-400">신청일: {member.createdAt}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => approveMember(member.id)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" /> 승인
                </button>
                <button
                  onClick={() => rejectMember(member.id)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> 거절
                </button>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* 연장 신청 대기 */}
      {pendingRenewals.length > 0 && (
        <Section icon={RefreshCw} title="연장 신청 대기" color="bg-teal-500" count={pendingRenewals.length}>
          {pendingRenewals.map(req => (
            <div key={req.id} className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-teal-700 dark:text-teal-400">
                    {(req.memberName || '?')[0]}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{req.memberName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {req.memberPhone && formatPhone(req.memberPhone)}
                    {req.productName && ` · ${req.productName}`}
                  </p>
                  {req.note && <p className="text-xs text-gray-400">"{req.note}"</p>}
                  <p className="text-xs text-gray-400">
                    신청일: {req.createdAt ? format(parseISO(req.createdAt), 'M월 d일', { locale: ko }) : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => approveRenewal(req.id)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" /> 확인
                </button>
                <button
                  onClick={() => rejectRenewal(req.id)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> 거절
                </button>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* 정보 수정 신청 대기 */}
      {pendingProfileEdits.length > 0 && (
        <Section icon={Edit3} title="정보 수정 신청 대기" color="bg-violet-500" count={pendingProfileEdits.length}>
          {pendingProfileEdits.map(req => {
            const fieldLabels = {
              phone: '전화번호', address: '주소', purpose: '운동 목적',
              birthDate: '생년월일', birth_date: '생년월일', gender: '성별',
            };
            const changeEntries = Object.entries(req.changes || {});
            return (
              <div key={req.id} className="px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-violet-700 dark:text-violet-400">
                        {(req.memberName || '?')[0]}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white text-sm">{req.memberName}</p>
                      <div className="mt-1 space-y-0.5">
                        {changeEntries.map(([field, val]) => {
                          const label = fieldLabels[field] || field;
                          const before = typeof val === 'object' && val !== null && 'before' in val ? val.before : '';
                          const after = typeof val === 'object' && val !== null && 'after' in val ? val.after : val;
                          return (
                            <p key={field} className="text-xs text-gray-500 dark:text-gray-400">
                              <span className="font-medium">{label}:</span> {before && <span className="line-through text-gray-400">{before}</span>}{before && ' → '}{after}
                            </p>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        신청일: {req.createdAt ? format(parseISO(req.createdAt), 'M월 d일', { locale: ko }) : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => approveProfileEdit(req.id)}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                    >
                      <Check className="w-3.5 h-3.5" /> 승인
                    </button>
                    <button
                      onClick={() => rejectProfileEdit(req.id)}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" /> 거절
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </Section>
      )}

      {/* 만료 임박 */}
      {expiringMemberships.length > 0 && (
        <Section icon={AlertTriangle} title="회원권 만료 임박" color="bg-yellow-500" count={expiringMemberships.length}>
          {expiringMemberships.map(ms => (
            <div
              key={ms.id}
              className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              onClick={() => navigate(`/members/${ms.memberId}`)}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">{ms.member.name[0]}</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{ms.member.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{ms.productName} · {formatPhone(ms.member.phone)}</p>
                  <p className="text-xs text-gray-400">만료일: {formatDate(ms.endDate)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <SmsButton text={`[${gymName}] ${ms.member.name}님, 회원권이 ${ms.days === 0 ? '오늘' : `${ms.days}일 후`} 만료됩니다. 재등록 문의: ${gymSettings?.phone || ''}`} />
                <span className={`font-bold text-sm px-3 py-1.5 rounded-full ${
                  ms.days === 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                  ms.days <= 3 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                }`}>
                  {ms.days === 0 ? 'D-Day' : `D-${ms.days}`}
                </span>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* 오늘 생일 */}
      {birthdayMembers.length > 0 && (
        <Section icon={Gift} title="오늘 생일인 회원" color="bg-pink-500" count={birthdayMembers.length}>
          {birthdayMembers.map(member => (
            <div
              key={member.id}
              className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              onClick={() => navigate(`/members/${member.id}`)}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-pink-700 dark:text-pink-400">{member.name[0]}</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{member.name} 🎂</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{formatPhone(member.phone)} · {member.birthDate}</p>
                </div>
              </div>
              <SmsButton text={`[${gymName}] ${member.name}님, 생일을 축하드립니다! 🎂 오늘 하루도 행복하세요. - ${gymName}`} />
            </div>
          ))}
        </Section>
      )}

      {/* PT 잔여 횟수 부족 */}
      {lowPtMembers.length > 0 && (
        <Section icon={Dumbbell} title="PT 횟수 부족 (5회 이하)" color="bg-purple-500" count={lowPtMembers.length}>
          {lowPtMembers.map(pt => (
            <div
              key={pt.id}
              className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              onClick={() => navigate(`/members/${pt.memberId}`)}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">{pt.member.name[0]}</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{pt.member.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{pt.productName} · {pt.trainerName} 트레이너</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <SmsButton text={`[${gymName}] ${pt.member.name}님, PT 잔여 횟수가 ${pt.remaining}회 남았습니다. 추가 등록 문의: ${gymSettings?.phone || ''}`} />
                <span className={`font-bold text-sm px-3 py-1.5 rounded-full ${
                  pt.remaining === 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                  pt.remaining <= 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                }`}>
                  잔여 {pt.remaining}회
                </span>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* 문자 템플릿 */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-500 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <h2 className="font-semibold text-gray-900 dark:text-white">문자 템플릿</h2>
        </div>
        <div className="p-4 space-y-3">
          {[
            { label: '등록 환영', text: `[${gymName}] 안녕하세요! 회원 등록을 환영합니다. 즐거운 운동 생활 응원합니다. 문의: ${gymSettings?.phone || ''}` },
            { label: '재등록 안내', text: `[${gymName}] 안녕하세요! 회원권 재등록 기간입니다. 빠른 재등록 시 혜택이 있으니 방문해 주세요. 문의: ${gymSettings?.phone || ''}` },
            { label: '휴관 안내', text: `[${gymName}] 안녕하세요. 사정으로 인해 임시 휴관합니다. 불편을 드려 죄송합니다. 문의: ${gymSettings?.phone || ''}` },
            { label: 'PT 일정 확인', text: `[${gymName}] 안녕하세요! 다음 PT 일정을 확인해 주세요. 변경이 필요하시면 미리 연락주세요.` },
          ].map(({ label, text }) => (
            <div key={label} className="flex items-start justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">{label}</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 break-words">{text}</p>
              </div>
              <SmsButton text={text} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
